import React from 'react';
import { Checkbox, Col, Form, Input, Radio, Row, Space, Typography } from 'antd';
import type { FormInstance } from 'antd';
import CheckAllButtons from '../CheckAllButtons';
import WeldMetaBlock from '../WeldMetaBlock';
import { SECTION_HEADER_WIDTH } from '../types';
import type { WeldMeta } from '../types';

const { Text } = Typography;

export interface PtPanelProps {
  form: FormInstance;
  weldKey: string;
  isView: boolean;
  meta?: WeldMeta;
}

/** PT (Penetrant Test) detay paneli — JSX PipeInspectionForm'dan birebir taşındı. */
export default function PtPanel({ form, weldKey, isView, meta }: PtPanelProps) {
  return (
    <div>
      {/* Weld meta info from RFI (read-only) */}
      <WeldMetaBlock meta={meta} showFluid={false} />
      <div style={{ padding: 4, backgroundColor: '#fffbe6', borderRadius: 4 }}>
        <Row gutter={[6, 4]}>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Preparation</Text>
              <Form.Item name={['results', weldKey, 'pt', 'preparation', 'surface_cleaned']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Surface cleaned & free of contaminants</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Application</Text>
              <Form.Item name={['results', weldKey, 'pt', 'application', 'penetrant_type']} style={{ margin: 0 }}>
                <Radio.Group size="small" disabled={isView}>
                  <Radio value="dye">Dye</Radio>
                  <Radio value="fluorescent">Fluorescent</Radio>
                </Radio.Group>
              </Form.Item>
              <Text>Dwell Time</Text>
              <Form.Item name={['results', weldKey, 'pt', 'application', 'dwell_time_min']} style={{ margin: 0 }}>
                <Input size="small" style={{ width: 80 }} type="number" step="1" placeholder="min" disabled={isView} />
              </Form.Item>
              <Form.Item name={['results', weldKey, 'pt', 'application', 'surface_cleaned_no_residue']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Surface Cleaned, no residue</Checkbox>
              </Form.Item>
            </Space>
          </Col>

          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Developer</Text>
              <Text>Type</Text>
              <Form.Item name={['results', weldKey, 'pt', 'developer', 'type']} style={{ margin: 0 }}>
                <Input size="small" style={{ width: 160 }} placeholder="e.g. dry/wet, brand" disabled={isView} />
              </Form.Item>
              <Text>Development time</Text>
              <Form.Item name={['results', weldKey, 'pt', 'developer', 'development_time_min']} style={{ margin: 0 }}>
                <Input size="small" style={{ width: 100 }} type="number" step="1" placeholder="min" disabled={isView} />
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Indications</Text>
              <Form.Item name={['results', weldKey, 'pt', 'indications', 'no_cracks']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>No cracks</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'pt', 'indications', 'proper_fusion']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Proper fusion</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'pt', 'indications', 'no_porosity']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>No porosity</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'pt', 'indications', 'no_laps']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>No laps</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Finish</Text>
              <Form.Item name={['results', weldKey, 'pt', 'finish', 'post_cleaning']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Post‑cleaning done</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="start" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Remarks</Text>
              <Form.Item name={['results', weldKey, 'pt', 'remarks']} style={{ margin: 0, flex: 1 }}>
                <Input.TextArea autoSize={{ minRows: 1, maxRows: 6 }} style={{ minWidth: 480 }} disabled={isView} />
              </Form.Item>
              {!isView && (
                <CheckAllButtons
                  typeLabel="PT"
                  onSetAll={(v) => {
                    const k = weldKey;
                    form.setFieldsValue({
                      results: {
                        [k]: {
                          pt: {
                            preparation: { surface_cleaned: v },
                            application: { ...(form.getFieldValue(['results', k, 'pt', 'application']) || {}), surface_cleaned_no_residue: v },
                            developer: form.getFieldValue(['results', k, 'pt', 'developer']),
                            indications: { no_cracks: v, proper_fusion: v, no_porosity: v, no_laps: v },
                            finish: { post_cleaning: v },
                          },
                        },
                      },
                    });
                  }}
                />
              )}
            </Space>
          </Col>
        </Row>
      </div>
    </div>
  );
}
